import { PayRequestPayload } from "checkout-query-client/api/types";
import {
  SavedCreditCardFieldNames,
  SavedEcpFieldNames,
  SavedSepaFieldNames,
} from "checkout-query-client/types/chex-form-fields";
import {
  DirtyFields,
  FieldValues,
  PaymentType,
} from "checkout-query-client/types/form-types";
import { buildPostalPayload } from "checkout-query-client/utils/build-payloads/build-base-payload";
import {
  getAcceptedMandates,
  getDeviceFingerprintSessionId,
  getExpirationMonthYear,
} from "checkout-query-client/utils/helpers";

import {
  BrowserInfo,
  CardUpdateDetails,
  EcpUpdateDetails,
  EncryptedStoredPaymentMethod,
  PayRequest,
  PayResponse_Status,
  PaymentMethod,
  PaymentMethod_Type,
  SepaUpdateDetails,
  StoredPaymentMethodUpdateDetails,
} from "frontend-client";

export const buildSavedPaymentPayload = async (
  payload: PayRequestPayload
): Promise<PayRequest> => {
  const {
    formFields,
    dirtyFields,
    paymentType,
    paymentMethodId,
    merchantSettings,
    checkoutSessionData,
    orderDigest,
  } = payload;
  const request = new PayRequest();
  const storedPaymentMethodDetails = new StoredPaymentMethodUpdateDetails();
  const browserInfo = new BrowserInfo();
  const { savedPayment } = getSavedPaymentPayload(formFields ?? {}) ?? {};
  const exisitingPaymentMethodData = checkoutSessionData?.paymentMethods?.find(
    (mop) => mop.paymentMethodId === paymentMethodId
  );

  if (paymentType && formFields) {
    // We do not need to send a Billing Address if it is disabled
    if (
      (paymentType === PaymentType.creditCard &&
        merchantSettings?.paymentMethods?.cardPaymentMethod
          ?.billingAddressCollection?.enabled) ||
      (paymentType === PaymentType.eCheck &&
        merchantSettings?.paymentMethods?.ecpPaymentMethod
          ?.billingAddressCollection?.enabled) ||
      (paymentType === PaymentType.sepa &&
        merchantSettings?.paymentMethods?.sepaPaymentMethod
          ?.billingAddressCollection?.enabled)
    ) {
      storedPaymentMethodDetails.billingAddress = buildPostalPayload(
        formFields,
        paymentType,
        payload.paymentMethodId ?? ""
      );
      storedPaymentMethodDetails.billingAddressId =
        exisitingPaymentMethodData?.billingAddressId ?? "";
    }

    if (
      paymentType === PaymentType.creditCard &&
      savedPayment.expirationMonth
    ) {
      const card = new CardUpdateDetails();
      card.expirationMonth = savedPayment.expirationMonth;
      card.expirationYear = savedPayment.expirationYear;
      card.cardHolderName = savedPayment.cardHolderName;
      storedPaymentMethodDetails.paymentMethodDetails = {
        case: "card",
        value: card,
      };
    } else if (paymentType === PaymentType.eCheck) {
      const ecp = new EcpUpdateDetails({
        accountHolderName: formFields.nameOnAccount,
      });
      storedPaymentMethodDetails.paymentMethodDetails = {
        case: "ecp",
        value: ecp,
      };
    } else if (paymentType === PaymentType.sepa) {
      const sepa = new SepaUpdateDetails({
        accountHolderName: formFields.sepaNameOnAccount,
      });
      storedPaymentMethodDetails.paymentMethodDetails = {
        case: "sepa",
        value: sepa,
      };
    }

    storedPaymentMethodDetails.setAsDefault =
      formFields?.setAsDefault === "true" || savedPayment.isDefault;
  }

  const savedPaymentRequest = new EncryptedStoredPaymentMethod();
  savedPaymentRequest.updateDetails = areSavePaymentFieldsDirty(dirtyFields)
    ? storedPaymentMethodDetails
    : undefined;
  savedPaymentRequest.paymentMethodId = paymentMethodId ?? "";

  // This is sending the encrypted CVV on DIU
  if (payload.experienceType === "dropInUI") {
    savedPaymentRequest.encryptedCardCvv = savedPayment.cardCvv;
    savedPaymentRequest.encryptionKeyId =
      merchantSettings?.encryptionKeyId ?? "";
  } else {
    savedPaymentRequest.cardCvv = savedPayment.cardCvv;
  }

  const paymentMethod = new PaymentMethod();
  paymentMethod.storedPaymentMethod = savedPaymentRequest;
  paymentMethod.type = PaymentMethod_Type.STORED_PAYMENT_METHOD;
  request.paymentMethod = paymentMethod;
  request.acceptedMandates = getAcceptedMandates(formFields);
  request.billingAddressFromIntentBilling =
    Boolean(checkoutSessionData?.billingAddress) &&
    !merchantSettings?.paymentMethods?.cardPaymentMethod
      ?.billingAddressCollection?.enabled;

  request.scaChallengeWindowSize = 4;
  request.makeDefaultPaymentMethod = formFields?.setAsDefault === "true";
  const date = new Date();
  browserInfo.acceptHeader = "text/html;charset=UTF-8";
  browserInfo.language = window.navigator.language || "";
  browserInfo.colorDepth = window.screen.colorDepth || 0;
  browserInfo.screenHeight = window.screen.height || 0;
  browserInfo.screenWidth = window.screen.width || 0;
  browserInfo.timezoneOffset = date.getTimezoneOffset() || 0;
  request.browserInfo = browserInfo;
  request.orderDigest = orderDigest ?? "";
  request.deviceFingerprintSessionId = getDeviceFingerprintSessionId();

  return await Promise.resolve(request);
};

const getSavedPaymentPayload = (
  formFields: FieldValues<SavedCreditCardFieldNames>
) => {
  let expirationMonth;
  let expirationYear;
  if (formFields?.expiration) {
    const expiration = getExpirationMonthYear(formFields?.expiration);
    expirationMonth = expiration.expirationMonth;
    expirationYear = expiration.expirationYear;
  } else {
    expirationMonth = Number(formFields?.expirationMonth);
    expirationYear = Number(formFields?.expirationYear);
  }

  return {
    status: PayResponse_Status.SUCCESS,
    savedPayment: {
      expirationMonth,
      expirationYear,
      cardCvv: formFields?.securityCode ?? "",
      isDefault: formFields?.setAsDefault === "true",
      cardHolderName: formFields?.nameOnCard ?? "",
    },
  };
};

export const areSavePaymentFieldsDirty = (
  dirtyFields?: DirtyFields<
    SavedCreditCardFieldNames | SavedSepaFieldNames | SavedEcpFieldNames
  >
) => {
  const concernedFields = [
    "billingTitle",
    "billingAddressCountry",
    "billingAddressZip",
    "billingAddressCity",
    "billingAddressAddressLine1",
    "billingAddressAddressLine2",
    "nameOnCard",
    "expiration",
    "expirationMonth",
    "expirationYear",
    "sepaNameOnAccount",
    "nameOnAccount",
    "setAsDefault",
  ] as Array<
    SavedCreditCardFieldNames | SavedSepaFieldNames | SavedEcpFieldNames
  >;
  return (
    dirtyFields &&
    Object.keys(dirtyFields).reduce((isDirty, field) => {
      const typedField = field as SavedCreditCardFieldNames;
      return Boolean(
        isDirty ||
          (concernedFields.includes(typedField) && dirtyFields[typedField])
      );
    }, false)
  );
};
